<script setup lang="ts">
import type { Header, TheTableObj } from '~/types'

defineProps({
  modelValue: null,
  title: null,
  headers: null,
  canSearch: { type: Boolean },
  searchParameter: null,
  canAdd: { type: Boolean },
  canDelete: { type: Boolean },
  noBreadcrumbs: { type: Boolean },
  noCheckbox: { type: Boolean },
  pageCount: null
})
const emits = defineEmits(["clickRow", "clickAdd", "clickDelete"])

const search = ref('')
const sortedBy = ref('id')
const sortedByDirection = ref(1)
const page = ref(1)
const perPage = __props.pageCount || 10
const selected = ref<Array<any>>([])

function nextPage() {
  if (page.value !== Math.ceil(__props.modelValue ? __props.modelValue.length / perPage : 1))
    page.value += 1
}

function backPage() {
  if (page.value !== 1)
    page.value -= 1
}

function goToPage(numPage: number) {
  page.value = numPage
}

function alphabetically(ascending: boolean, sortedBy: string) {
  return function (a: any, b: any) {
    const aValue = a[sortedBy]
    const bValue = b[sortedBy]

    // equal items sort equally
    if (aValue === bValue)
      return 0

    // nulls sort after anything else
    if (aValue === null)
      return 1

    if (bValue === null)
      return -1

    // otherwise, if we're ascending, lowest sorts first
    if (ascending)
      return aValue < bValue ? -1 : 1

    // if descending, highest sorts first
    return aValue < bValue ? 1 : -1
  }
}

const sortedData = computed(() => {
  if (!__props.modelValue)
    return []

  if (__props.searchParameter && search.value) {
    goToPage(1)
    return __props.modelValue.filter(
      value => (value as any)[__props.searchParameter]
        .toLowerCase()
        .includes(search.value.toLowerCase()),
    )
  }
  else {
    const subArray = __props.modelValue.slice(0)
    return subArray.sort(alphabetically(sortedByDirection.value === 1, sortedBy.value))
  }
})

const paginatedData = computed(() => {
  if (!sortedData.value)
    return []
  return sortedData.value.slice((page.value - 1) * perPage, page.value * perPage)
})

function select(event: Event, obj: any) {
  const element = event.target as HTMLInputElement
  if (element.checked) {
    selected.value.push(clone(obj))
  }
  else {
    const idx = selected.value.map(a => a.id).indexOf(obj.id)
    if (idx !== -1)
      selected.value.splice(idx, 1)
  }
}

function selectAll(event: Event) {
  const element = event.target as HTMLInputElement
  if (element.checked) {
    selected.value.splice(0)
    selected.value.push(...clone(__props.modelValue))
    __props.modelValue.forEach((element) => {
      element.selected = true
    })
  }
  else {
    selected.value.splice(0)
    __props.modelValue.forEach((element) => {
      element.selected = false
    })
  }
}

function sortByColumn(col: string) {
  if (sortedBy.value && sortedBy.value === col) {
    if (sortedByDirection.value === -1) {
      sortedByDirection.value = 1 // same column, undo sort
      sortedBy.value = ''
    }
    else {
      sortedByDirection.value = sortedByDirection.value * -1 // same column, reverse order
    }
  }
  else {
    sortedByDirection.value = 1
    sortedBy.value = col
  }
}
</script>

<template>
  <div>
    <div v-if="title || canSearch || canAdd || canDelete" class="block flex flex-col items-center justify-between border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div class="w-full" :class="!noBreadcrumbs ? [] : ['mt--2.5']">
        <div class="h-16 flex items-center justify-between">
          <h1 v-if="title" class="border-b text-xl text-gray-900 font-semibold sm:text-2xl dark:text-white">
            {{ title }}
          </h1>

          <div class="sm:flex">
            <div class="mb-0 hidden flex items-center">
              <slot name="filter" />
              <form v-if="canSearch" class="pr-2">
                <label for="users-search" class="sr-only">Suche</label>
                <div class="relative mt-1 lg:w-64 xl:w-96">
                  <input id="users-search" v-model="search" type="text" name="users-search" class="focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-500 dark:focus:border-primary-500 block w-full border border-gray-300 rounded-lg bg-gray-50 p-2.5 text-gray-900 dark:border-gray-600 dark:bg-gray-700 sm:text-sm dark:text-white dark:placeholder-gray-400" placeholder="Suchen" @keydown.enter.exact.stop.prevent="">
                </div>
              </form>
              <div v-if="canAdd || canDelete" class="flex pl-0 sm:mt-0 space-x-1 sm:pl-2">
                <button
                  v-if="canAdd"
                  type="button"
                  class="mr-2 px-5 py-2.5 sb-button"
                  @click="emits('clickAdd')"
                >
                  <div class="sb-button-icon" i-mdi-plus />
                  Neu
                </button>
              </div>
              <slot name="buttons" />
            </div>
          </div>
        </div>
      </div>

      <slot name="header" />
    </div>

    <!-- Table -->
    <div class="flex flex-col">
      <div class="overflow-x-auto">
        <div class="inline-block min-w-full align-middle">
          <div class="overflow-hidden shadow">
            <table class="min-w-full table-fixed divide-y divide-gray-200 dark:divide-gray-600">
              <thead class="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th v-if="!noCheckbox" scope="col" class="p-4 px-2">
                    <div class="flex items-center">
                      <input id="checkbox-all" aria-describedby="checkbox-1" type="checkbox" class="focus:ring-primary-300 dark:focus:ring-primary-600 h-4 w-4 border-gray-300 rounded bg-gray-50 dark:border-gray-600 dark:bg-gray-700 focus:ring-3 dark:ring-offset-gray-800" @change="selectAll">
                      <label for="checkbox-all" class="sr-only">checkbox</label>
                    </div>
                  </th>
                  <!-- Custom Header -->
                  <th v-for="header in headers" :key="header.value" scope="col" class="p-4 px-2 text-left text-xs text-gray-500 font-medium dark:text-gray-400">
                    <div class="flex cursor-pointer items-center">
                      <span class="select-none" @click="sortByColumn(header.value)">{{ header.text }}</span>
                      <a class="px-2" @click="sortByColumn(header.value)">
                        <div v-if="sortedBy !== header.value" i-mdi-sort class="h-5 w-5" />
                        <div v-if="sortedBy === header.value && sortedByDirection === 1" i-mdi-sort-ascending class="h-5 w-5" />
                        <div v-if="sortedBy === header.value && sortedByDirection === -1" i-mdi-sort-descending class="h-5 w-5" />
                      </a>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                <tr v-for="model in paginatedData" :key="model.id" class="hover:cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                  <td v-if="!noCheckbox" class="w-4 p-2">
                    <div class="flex items-center">
                      <input id="checkbox-1" v-model="model.selected" aria-describedby="checkbox-1" type="checkbox" class="focus:ring-primary-300 dark:focus:ring-primary-600 h-4 w-4 border-gray-300 rounded bg-gray-50 dark:border-gray-600 dark:bg-gray-700 focus:ring-3 dark:ring-offset-gray-800" @change="select($event, model)">
                      <label for="checkbox-1" class="sr-only">checkbox</label>
                    </div>
                  </td>

                  <td
                    v-for="header in headers"
                    :key="header.value + model.id"
                    class="max-w-sm overflow-hidden truncate p-2 text-left text-base text-gray-500 font-normal xl:max-w-xs dark:text-gray-200"
                    @click="emits('clickRow', model)"
                  >
                    <template v-if="header.type === 'date'">
                      {{ formatDate((model as any)[header.value]) }}
                    </template>
                    <template v-else-if="header.type === 'datetime'">
                      {{ formatDatetime((model as any)[header.value]) }}
                    </template>
                    <template v-else-if="!!header.getString">
                      {{ header.getString((model as any)[header.value]) }}
                    </template>
                    <template v-else-if="header.type === 'html'">
                      <div v-html="(model as any)[header.value]" />
                    </template>
                    <template v-else>
                      {{ (model as any)[header.value] }}
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="sticky bottom-0 right-0 w-full items-center border-t border-gray-200 bg-white p-4 sm:flex sm:justify-between dark:border-gray-700 dark:bg-gray-800">
      <div class="mb-4 flex items-center sm:mb-0">
        <a href="#" class="inline-flex cursor-pointer justify-center rounded p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white" @click.exact.stop.prevent="backPage">
          <div class="h-7 w-7" i-mdi-chevron-left />
        </a>
        <a href="#" class="mr-2 inline-flex cursor-pointer justify-center rounded p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white" @click.exact.stop.prevent="nextPage">
          <div class="h-7 w-7" i-mdi-chevron-right />
        </a>
        <span class="text-sm text-gray-500 font-normal dark:text-gray-400">
          Zeige <span class="text-gray-900 font-semibold dark:text-white">
            {{ ((page - 1) * perPage) + 1 }}-{{ page * perPage }}
          </span> von <span class="text-gray-900 font-semibold dark:text-white">
            {{ modelValue.length }}
          </span>
        </span>
      </div>
      <div class="flex items-center space-x-3">
        <a href="#" class="inline-flex flex-1 items-center justify-center rounded-lg px-3 py-2 text-center text-sm text-gray-500 font-medium hover:bg-gray-100 dark:text-gray-400 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white" @click.exact.stop.prevent="backPage">
          <div i-mdi-chevron-left class="h-5 w-5" />
          Zurück
        </a>
        <a href="#" class="inline-flex flex-1 items-center justify-center rounded-lg px-3 py-2 text-center text-sm text-gray-500 font-medium hover:bg-gray-100 dark:text-gray-400 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white" @click.exact.stop.prevent="nextPage">
          Weiter
          <div i-mdi-chevron-right class="h-5 w-5" />
        </a>
      </div>
    </div>
  </div>
</template>
